import {
  type BillingFeatureOptions,
  type FetchBillingMethodsFn,
  type FetchBillingProfileFn,
  type NewPaymentMethodFn,
  type ProfilePaymentMethodsViewModelFactory,
  type SelectPaymentMethodFn,
  type StripeConnectCallbackModalViewModelFactory,
  type TeamPaymentsViewModelFactory,
} from '@/features/billing'
import { BillingApi } from '@/features/billing/api/billing-api'
import { NewPaymentMethodDialogViewModel } from '@/features/billing/payment-methods/NewPaymentMethodDialog/NewPaymentMethodDialogViewModel'
import { PaymentMethodSelectorDialogViewModel } from '@/features/billing/payment-methods/PaymentMethodSelectorDialog/PaymentMethodSelectorDialogViewModel'
import { StripeConnectCallbackModalViewModel } from '@/features/billing/stripe-connect/StripeConnectCallbackModalViewModel'
import { ProfilePaymentMethodsViewModel } from '@/features/billing/payment-methods/ProfilePaymentMethods/ProfilePaymentMethodsViewModel'
import { TeamPaymentsViewModel } from '@/features/billing/payment-methods/TeamPayments/TeamPaymentsViewModel'
import { BillingProfileStore } from '@/features/billing/profile/BillingProfileStore'
import { TransactionStore } from '@/features/billing/transactions/TransactionStore'
import { CouponStore } from '@/features/billing/coupons/CouponStore'
import { ExternalCheckoutStore } from '@/features/billing/external-checkout/ExternalCheckoutStore'
import { type Member } from '@/features/iam/members/Member'
import { Team } from '@/features/iam/teams/Team'
import { env } from '@/infra/env'

export function InternalBillingFeature(options: BillingFeatureOptions) {
  const billingApi = new BillingApi({
    baseURL: env.BILLING_API,
    token$: options.token$,
  })

  const couponStore = new CouponStore(billingApi)
  const transactionStore = new TransactionStore(billingApi)
  const externalCheckoutStore = new ExternalCheckoutStore(billingApi.checkout)

  const billingProfileStore = new BillingProfileStore(
    billingApi,
    options.getCurrentUser,
    options.getCurrentMember,
    options.getTeamMember,
    options.primeWorkspace,
    options.currentMember$,
  )

  const newPaymentMethodDialogVm = new NewPaymentMethodDialogViewModel(
    billingProfileStore,
  )

  const promptNewPaymentMethod: NewPaymentMethodFn = (member, team, signal) =>
    newPaymentMethodDialogVm.prompt(member, team, signal)

  const paymentMethodSelectorDialogVm =
    new PaymentMethodSelectorDialogViewModel(
      billingProfileStore,
      promptNewPaymentMethod,
      options.createAlertErrorHandler,
    )

  const promptSelectPaymentMethod: SelectPaymentMethodFn = (
    member,
    team,
    currentPaymentMethod,
    onSave,
    signal,
  ) =>
    paymentMethodSelectorDialogVm.prompt(
      member,
      team,
      currentPaymentMethod,
      onSave,
      signal,
    )

  const fetchBillingProfile: FetchBillingProfileFn = (owner) =>
    owner instanceof Team
      ? billingProfileStore.fetchBillingProfileForTeam(owner.id)
      : billingProfileStore.fetchBillingProfileForIndividual(
          owner.workspace.id,
          owner.user.publicId,
        )

  const fetchBillingMethods: FetchBillingMethodsFn = (owner) =>
    owner instanceof Team
      ? billingProfileStore.fetchBillingMethodsForTeam(owner)
      : billingProfileStore.fetchBillingMethodsForIndividual(owner)

  const getBillingProfile = (owner: Member | Team) =>
    (owner instanceof Team
      ? billingProfileStore.teamBillingProfile(owner)
      : billingProfileStore.individualBillingProfile(owner)) ?? undefined

  const getBillingMethods = (owner: Member | Team) =>
    owner instanceof Team
      ? billingProfileStore.billingMethodsForTeam(owner)
      : billingProfileStore.billingMethodsForIndividual(owner)

  const canManageBilling = (owner: Member | Team) => {
    const profile = getBillingProfile(owner)
    return profile ? billingProfileStore.canManage(profile) : false
  }

  const createProfilePaymentMethodsViewModel: ProfilePaymentMethodsViewModelFactory =
    (member) =>
      new ProfilePaymentMethodsViewModel(
        member,
        billingProfileStore,
        promptNewPaymentMethod,
        options.createAlertErrorHandler,
        options.createAlertRetryWrapper,
      )

  const createTeamPaymentsViewModel: TeamPaymentsViewModelFactory = (team) =>
    new TeamPaymentsViewModel(
      options.getCurrentMember,
      team,
      billingProfileStore,
      promptNewPaymentMethod,
      options.createAlertErrorHandler,
      options.createAlertRetryWrapper,
    )

  const createStripeConnectCallbackModalViewModel: StripeConnectCallbackModalViewModelFactory =
    (connectStripe) => new StripeConnectCallbackModalViewModel(connectStripe)

  return {
    // Internal
    fetchBillingProfile,
    getBillingProfile,
    canManageBilling,
    fetchBillingMethods,
    promptNewPaymentMethod,
    promptSelectPaymentMethod,
    getBillingMethods,
    createProfilePaymentMethodsViewModel,
    createTeamPaymentsViewModel,
    createStripeConnectCallbackModalViewModel,
    billingProfileStore,
    transactionStore,
    couponStore,
    externalCheckoutStore,
    newPaymentMethodDialogVm,
    paymentMethodSelectorDialogVm,
  }
}
